/**
 * Générateur — Il compare, range et encadre des nombres rationnels (positifs ou
 * négatifs).
 * Attendu ID : 143 | Niveau : 4ème | Domaine : Nombres (desc 11, O8)
 *
 * Source doc prof : Créé n°5 (ranger croissant, encadrer entre entiers, V/F) +
 * image p.12 (« Complète par >, < ou = : 5/18 … 7/12 ; 5/12 … 4/3 ; -3 … -22/7 »).
 * Plan de cartes validé 2026-07-14 :
 *
 *   Carte « completer » — Compare deux rationnels (qcm_unique : <, >, =)
 *   Carte « ranger » — Ranger dans l'ordre croissant (ordonner)
 *   Carte « encadrer » — {{a}} < x < {{a+1}} (saisie 2 trous)
 *   Carte « vf » — Vrai/Faux sur des comparaisons/encadrements (tableau_vf)
 *
 * Fractions à dénominateur positif, signe au numérateur ; décimaux à la
 * française. Comparaison EXACTE par produits en croix (entiers).
 * Le doc signale ⚠️ « plusieurs écritures » → toute valeur est UNIQUE par
 * construction (jamais deux items de même valeur).
 *
 * Revue 2026-09-22 (mesures sur dix mille tirages par carte) : aucune carte
 * supprimée, mais l'élève n'avait presque jamais à comparer vraiment
 * (« completer » : 39,8 % des tirages se décidaient au seul signe, 27,5 % à la
 * seule partie entière, 12,4 % demandaient une mise au même dénominateur).
 *   - « completer » et « vf » : les deux nombres comparés ont le même signe ET
 *     la même partie entière (helper pairProche, par construction) ;
 *     dénominateurs élargis à 2..12 (l'exemple officiel compare 5/18 et 7/12) ;
 *     un entier est admis comme second nombre, l'un des deux entiers qui
 *     encadrent la fraction (l'exemple officiel compare -3 et -22/7).
 *   - « ranger » : au moins deux des quatre valeurs non nulles partagent la même
 *     partie entière (39,0 % des tirages se rangeaient à la seule partie entière).
 *   - « encadrer », et les lignes d'encadrement de « vf » : plus de décimal
 *     POSITIF (« 2 < 2,5 < 3 » se lisait sans calcul, « -3 < -2,5 < -2 »
 *     demande de raisonner le signe). Les fractions restent des deux signes :
 *     encadrer 7/3 demande de chercher sa partie entière, ce n'est pas une
 *     lecture. Donc décimal négatif, fraction de signe quelconque.
 *   - Parenthèse explicative de la consigne « completer » réécrite en phrase ;
 *     les tableaux V/F portent toujours au moins une ligne vraie et une fausse.
 *
 * Revue 2026-09-23 (typographie, règle prof) : les fractions affichées passent
 * en KaTeX, empilées (helpers tex/fracTrouTex de generators/utils.js). Les
 * énoncés, les items de « ranger », les lignes des tableaux V/F et la phrase à
 * trous de « encadrer » (ligne KaTeX globale, les deux entiers en trous autour
 * de la fraction) sont concernés ; les réponses tapées restent en texte brut.
 * Un décimal s'écrit 2{,}5 (virgule collée en mode maths) ; un fragment qui
 * commence par un moins est groupé entre accolades, sinon le \mathrm{} posé
 * en tête par tex() fait du moins un opérateur binaire espacé (« − 7/3 »).
 * Les paramètres du harnais (_meta.params) gardent l'écriture ASCII.
 *
 * Règle prof 2026-09-23 (composition des tableaux vrai-faux) : quatre lignes
 * au maximum, sinon c'est trop long à lire. « vf » est d'abord passée de cinq
 * lignes à quatre, puis, le même jour, de quatre à TROIS : le professeur a vu
 * à l'écran une ligne du genre « -14/5 > -2,5 » et trouvait le tableau trop
 * lourd, chaque ligne demandant de mettre les deux nombres au même
 * dénominateur, et il y en avait quatre. Répartition : DEUX comparaisons (le
 * cœur de la carte) et UN encadrement ; la vérité des trois lignes est décidée
 * d'avance, au moins une vraie et une fausse par construction. Au plus une des
 * deux comparaisons est en mode « frac » (fraction contre fraction, le cas le
 * plus coûteux, il faut chercher un dénominateur commun) ; l'autre est en mode
 * « dec » ou « ent », où une division ou la lecture de la partie entière
 * suffit. Ce n'est pas une facilité : cette carte vérifie des comparaisons, et
 * savoir quel filtre s'applique fait partie du geste. La consigne dit en une
 * phrase que poser ses calculs au brouillon est permis (demande prof, voir
 * RAPPEL_BROUILLON et le commentaire de la carte).
 *
 * Échelon du bas 2026-09-23 (demande prof) : la carte « completer » compare,
 * un tirage sur dix (PART_SIGNES_OPPOSES), une fraction NÉGATIVE et une
 * fraction POSITIVE, dans la forme la plus pure : le même numérateur et le
 * même dénominateur des deux côtés, seul le signe change (-3/7 contre 3/7).
 * Aucun calcul, le signe décide : c'est le premier filtre du raisonnement
 * complet, celui que l'élève applique avant tout, et la même fraction des deux
 * côtés ne laisse aucune ambiguïté sur ce qui est évalué. Le côté qui porte le
 * négatif est tiré une fois sur deux à gauche, une fois sur deux à droite,
 * sinon la position trahirait la réponse ; la réponse est donc « < » ou « > »
 * à parts égales, jamais « = ». Ces tirages, et eux seuls, portent
 * contenu.difficulte = 'signes_opposes' (convention docs/FORMATS_TANOED.md
 * §0.8, même canal que gen_142) ; les autres tirages gardent la contrainte de
 * la revue 2026-09-22, même signe et même partie entière, sans aucun
 * changement. Rappel : cette revue avait RETIRÉ les signes différents de la
 * carte parce que 39,8 % des tirages se décidaient au seul signe, la carte ne
 * demandait presque jamais de comparer vraiment. Les remettre n'est légitime
 * que parce qu'ils sont à faible dose et marqués comme un échelon
 * identifiable. L'échelle complète de l'attendu est exportée
 * (ECHELONS_DIFFICULTE).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeOrdonner, makeTexteTrou, makeVraiFaux, tex,
} from '../../../utils';
import { pgcd } from './nombres';

const ATTENDU_ID = 143;

// ── Échelon du bas « signes opposés » (2026-09-23) ──────────────────────────
// Part des tirages de la carte completer où l'on compare une fraction négative
// et la même fraction positive : un tirage sur dix. Le professeur change ce
// nombre ici et nulle part ailleurs (0 = jamais, 1 = toujours).
const PART_SIGNES_OPPOSES = 1 / 10;
// Valeur du marqueur contenu.difficulte posé sur ces tirages (et eux seuls).
const DIFFICULTE_SIGNES_OPPOSES = 'signes_opposes';

/**
 * Les échelons de cet attendu, DU PLUS SIMPLE AU PLUS COÛTEUX : l'ordre du
 * tableau EST l'ordre de la progression. Donnée pure, jamais lue à l'exécution,
 * sur le modèle de ECHELONS_DIFFICULTE de gen_142 (convention du champ :
 * docs/FORMATS_TANOED.md §0.8) ; elle existe pour que la différenciation à
 * venir lise une liste au lieu de lire de la prose, et pour qu'un test puisse
 * vérifier que tout échelon `pose: true` est bien posé dans contenu.difficulte.
 *
 * `pose: false` = l'échelon SORT déjà des tirages, mais rien ne le marque, donc
 * on ne sait pas le retrouver : la carte tire ses dénominateurs librement entre
 * 2 et Q_MAX, et le cas « un dénominateur multiple de l'autre » comme le cas
 * « aucun des deux » sortent par hasard. Le jour où on différencie, il faudra
 * les poser et faire de leur taux un réglage, comme PART_SIGNES_OPPOSES.
 */
export const ECHELONS_DIFFICULTE = [
    {
        code: DIFFICULTE_SIGNES_OPPOSES,
        pose: true,
        geste: 'les deux nombres sont de signes contraires : le signe décide, sans aucun calcul',
    },
    {
        code: 'denominateurs_multiples',
        pose: false,
        geste: 'même signe et même partie entière, et un dénominateur est un multiple de l\'autre : une seule fraction à transformer pour les mettre au même dénominateur, le geste automatisé en 5e (attendu 1052)',
    },
    {
        code: 'denominateurs_quelconques',
        pose: false,
        geste: 'même signe et même partie entière, et aucun dénominateur n\'est multiple de l\'autre : chercher un dénominateur commun ou faire le produit en croix, le travail neuf de la 4e (exemple officiel 5/18 contre 7/12)',
    },
];

const L_COMPLETER = 'Comparer deux nombres rationnels';
const L_RANGER = 'Ranger des rationnels dans l\'ordre croissant';
const L_ENCADRER = 'Encadrer un rationnel entre deux entiers';
const L_VF = 'Vérifier des comparaisons de rationnels';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ── Rationnels EXACTS : {p, q} avec q > 0, comparaison par produits en croix ──
const cmpRat = (a, b) => {           // -1 si a<b, 0 si a=b, 1 si a>b
    const d = a.p * b.q - b.p * a.q; // q > 0 → signe direct
    return d < 0 ? -1 : (d > 0 ? 1 : 0);
};
const floorDiv = (p, q) => Math.floor(p / q);   // p, q petits entiers → exact

// Fraction irréductible signée, |valeur| non entière, dénominateur 2..12
// (revue 2026-09-22 : avant 2..9 ; l'exemple officiel compare 5/18 et 7/12,
// des dénominateurs qui ne sont pas multiples l'un de l'autre).
const Q_MAX = 12;
const fracAlea = () => {
    for (let i = 0; i < 60; i++) {
        const q = randInt(2, Q_MAX);
        const p = randInt(1, 3 * q) * randomChoice([1, -1]);
        if (p % q !== 0 && pgcd(p, q) === 1) return { p, q };
    }
    return { p: -7, q: 3 };
};
// Décimal à une décimale non nulle ; signe imposé (1 ou -1) ou tiré.
const decAlea = (signe = randomChoice([1, -1])) => {
    const ent = randInt(0, 3);
    const dec = randInt(1, 9);
    return { p: signe * (ent * 10 + dec), q: 10 };
};

// Affichages. Une VALEUR est { r, dec } : le rationnel exact et son registre
// d'écriture (dec = true pour un décimal, q = 10 par construction ; sinon une
// fraction, ou un entier si q = 1). L'écriture ASCII (disp) sert aux
// paramètres du harnais ; le fragment LaTeX (frag) est ce que l'élève lit
// (revue 2026-09-23), composé dans tex() ou dans une ligne KaTeX globale.
const vFrac = (r) => ({ r, dec: false });
const vDec = (r) => ({ r, dec: true });
const dispFrac = (r) => (r.q === 1 ? String(r.p) : `${r.p}/${r.q}`);   // « -3/4 », « -3 »
const dispDec = (r) => {                                  // { p, q=10 } → « -0,7 »
    const neg = r.p < 0; const a = Math.abs(r.p);
    const ent = Math.floor(a / 10); const d = a % 10;
    return `${neg ? '-' : ''}${ent},${d}`;
};
const disp = (v) => (v.dec ? dispDec(v.r) : dispFrac(v.r));
const fragFrac = (r) => {                                 // « -\dfrac{3}{4} », « -3 »
    const s = r.p < 0 ? '-' : '';
    const a = Math.abs(r.p);
    return r.q === 1 ? `${s}${a}` : `${s}\\dfrac{${a}}{${r.q}}`;
};
const frag = (v) => (v.dec ? dispDec(v.r).replace(',', '{,}') : fragFrac(v.r));   // « -0{,}7 »
// Un fragment qui commence par un moins est groupé : après le \mathrm{} de
// tex() (ou en tête de ligne), un moins nu serait un opérateur binaire espacé.
const grp = (f) => (f.startsWith('-') ? `{${f}}` : f);
const K = (v) => tex(grp(frag(v)));                        // une valeur seule, en $…$

// ── Paire « proche » (revue 2026-09-22) ─────────────────────────────────────
// Deux nombres qui ne se départagent ni par le signe ni par la partie entière :
// une fraction A et, selon le mode, un décimal ('dec') ou une fraction ('frac')
// de même signe et de même partie entière, ou l'un des deux entiers qui
// encadrent A ('ent', jamais 0). B est construit directement dans l'intervalle
// d'entiers de A ; boucle bornée, repli déterministe par mode.
const REPLI_PAIRE = {
    dec: { A: vFrac({ p: -7, q: 3 }), B: vDec({ p: -25, q: 10 }) },
    frac: { A: vFrac({ p: -7, q: 3 }), B: vFrac({ p: -5, q: 2 }) },
    ent: { A: vFrac({ p: -22, q: 7 }), B: vFrac({ p: -3, q: 1 }) },
};
const pairProche = (mode) => {
    for (let i = 0; i < 60; i++) {
        const A = fracAlea();
        const f = floorDiv(A.p, A.q);                       // A ∈ ]f ; f + 1[
        let B;
        if (mode === 'ent') {
            // f et f + 1 sont du signe de A, sauf 0 que l'on écarte
            const n = randomChoice([f, f + 1].filter(k => k !== 0));
            B = vFrac({ p: n, q: 1 });
        } else if (mode === 'dec') {
            // partie entière de |B| choisie pour que la partie entière de B soit f
            const ent = f >= 0 ? f : -f - 1;
            B = vDec({ p: (A.p < 0 ? -1 : 1) * (ent * 10 + randInt(1, 9)), q: 10 });
        } else {
            const q = randInt(2, Q_MAX);
            const p = f * q + randInt(1, q - 1);            // p/q ∈ ]f ; f + 1[, du signe de A
            if (pgcd(p, q) !== 1) continue;
            B = vFrac({ p, q });
        }
        if (cmpRat(A, B.r) === 0) continue;
        return { A: vFrac(A), B };
    }
    return REPLI_PAIRE[mode];
};

// ═════════ Carte « completer » — Compare deux rationnels (<, >, =) ═════════

// Marqueur de l'échelon du bas, posé seulement sur les tirages à signes
// opposés : contenu.difficulte survit à l'enregistrement en base (contenu est
// persisté, _meta est effacé au POST, seul generatorKey en survit) ; recopié
// dans _meta.params pour le harnais et les dumps.
const marquerSignesOpposes = (payload) => {
    payload.contenu = { ...payload.contenu, difficulte: DIFFICULTE_SIGNES_OPPOSES };
    payload._meta.params = { ...payload._meta.params, difficulte: DIFFICULTE_SIGNES_OPPOSES };
    return payload;
};

const genCompleterQcm = () => {
    let A; let B;
    const signesOpposes = Math.random() < PART_SIGNES_OPPOSES;
    if (signesOpposes) {
        // ÉCHELON DU BAS (2026-09-23) : la même fraction irréductible des deux
        // côtés, négative d'un côté et positive de l'autre, le signe décide.
        // Le côté du négatif est tiré une fois sur deux, sinon la position
        // trahirait la réponse ; jamais d'égalité ici.
        const { p, q } = fracAlea();
        const a = Math.abs(p);
        const neg = vFrac({ p: -a, q });
        const pos = vFrac({ p: a, q });
        [A, B] = randomChoice([true, false]) ? [neg, pos] : [pos, neg];
    } else {
        const mode = randInt(1, 5);
        if (mode === 1) {
            // ÉGALITÉ : une fraction et une écriture non réduite de la même valeur.
            const { p, q } = fracAlea();
            const k = randInt(2, 4);
            A = vFrac({ p, q });
            B = vFrac({ p: p * k, q: q * k });
        } else {
            // Même signe et même partie entière : fraction contre décimal (2),
            // contre un entier voisin (3), contre une fraction (4 et 5).
            ({ A, B } = pairProche(mode === 2 ? 'dec' : (mode === 3 ? 'ent' : 'frac')));
        }
    }
    const c = cmpRat(A.r, B.r);
    const correct = c < 0 ? '<' : (c > 0 ? '>' : '=');
    const texte = `<p>On compare ${tex(`A = ${frag(A)}`)} et ${tex(`B = ${frag(B)}`)}.</p>`
        + '<p><b>Choisis le symbole qui complète A … B.</b></p>';
    const payload = makeQcm(['<', '>', '='], correct, texte, opts(L_COMPLETER));
    payload._meta.params = { dispA: disp(A), dispB: disp(B), correct };
    return signesOpposes ? marquerSignesOpposes(payload) : payload;
};

// ═════════ Carte « ranger » — Ordre croissant (ordonner) ═════════

const REPLI_RANGER = [
    vFrac({ p: -7, q: 3 }), vDec({ p: -25, q: 10 }), vDec({ p: 4, q: 10 }), vFrac({ p: 3, q: 4 }),
];
// Quatre valeurs distinctes : un zéro parfois, des fractions et des décimaux,
// des deux signes (moins de quatre si les tirages se répètent : l'appelant retire).
const tirerRanger = () => {
    const cand = [];
    const pushIf = (v) => {
        if (cand.every(c => cmpRat(c.r, v.r) !== 0)) cand.push(v);
    };
    if (randomChoice([true, false])) pushIf(vFrac({ p: 0, q: 1 }));
    for (let i = 0; i < 12 && cand.length < 4; i++) {
        if (randomChoice([true, false])) pushIf(vFrac(fracAlea()));
        else pushIf(vDec(decAlea()));
    }
    return cand.slice(0, 4);
};

const genRangerOrdonner = () => {
    let sel = REPLI_RANGER;
    for (let essai = 0; essai < 60; essai++) {
        const cand = tirerRanger();
        if (cand.length < 4) continue;
        // Au moins deux valeurs non nulles dans le même intervalle d'entiers,
        // sinon l'ordre se lit à la seule partie entière (revue 2026-09-22).
        const parties = cand.filter(c => c.r.p !== 0).map(c => floorDiv(c.r.p, c.r.q));
        if (new Set(parties).size === parties.length) continue;
        sel = cand;
        break;
    }
    const tries = [...sel].sort((a, b) => cmpRat(a.r, b.r));
    const payload = makeOrdonner(tries.map(K),
        '<p><b>Range ces nombres dans l\'ordre croissant</b>, du plus petit au plus grand.</p>',
        opts(L_RANGER));
    payload._meta.params = { ordre: tries.map(disp) };
    return payload;
};

// ═════════ Carte « encadrer » — {{a}} < x < {{a+1}} (saisie) ═════════

const genEncadrerTrous = () => {
    // Plus de décimal POSITIF (revue 2026-09-22) : « 2 < 2,5 < 3 » se lisait
    // sans calcul, « -3 < -2,5 < -2 » demande de raisonner le signe. La
    // fraction, elle, garde les deux signes : encadrer 7/3 demande de chercher
    // sa partie entière, ce n'est pas une lecture.
    const useFrac = randomChoice([true, false]);
    const x = useFrac ? vFrac(fracAlea()) : vDec(decAlea(-1));
    const a = floorDiv(x.r.p, x.r.q);
    // Ligne KaTeX globale (entière entre $…$, revue 2026-09-23) : les deux
    // entiers sont les trous, la fraction empilée entre eux. Les mots des trous
    // restent en texte brut (« -1 », « 0 »).
    const trou = (n) => `{{${n}}}`;
    const phrase = `$${trou(a)} \\lt ${frag(x)} \\lt ${trou(a + 1)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On veut encadrer ${K(x)} entre deux entiers consécutifs.</p>`
        + `<p><b>Complète les deux entiers</b> : ${tex(`\\ldots \\lt ${frag(x)} \\lt \\ldots`)}.</p>`,
        { ...opts(L_ENCADRER), mode: 'saisie' });
    payload._meta.params = { disp: disp(x), a };
    return payload;
};

// ═════════ Carte « vf » — Vrai/Faux (tableau) ═════════

// Une phrase de consigne dit que poser ses calculs au brouillon est permis
// (demande prof 2026-09-23, assumée) : une vraie phrase, pas une parenthèse,
// et pas un indice de méthode (ni dénominateur commun ni conversion). Même
// forme que les rappels RAPPEL_BROUILLON déjà posés dans le parc (6e 613 à
// 619, 5e 1044, 1045 et 1061 : ligne grise en italique avant la consigne) ; le
// mot apparaît aussi dans six coups de pouce. La place naturelle de cette
// phrase serait le coup de pouce de cet attendu, qui n'existe pas encore :
// elle pourra migrer là le jour où il sera écrit.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Tu peux poser tes calculs au brouillon.</p>';

const genVfTableau = () => {
    const lignes = [];
    const bonnes = [];
    // Jamais deux lignes identiques : rend false pour que l'appelant retire.
    const pousser = (txt, vrai) => {
        if (lignes.includes(txt)) return false;
        lignes.push(txt); bonnes.push(vrai ? 0 : 1);
        return true;
    };
    // Tire une ligne jusqu'à vingt fois (unicité), puis pose le repli
    // déterministe : le tableau a toujours exactement trois lignes.
    const ajouter = (tirer, repli, vrai) => {
        for (let essai = 0; essai < 20; essai++) {
            if (pousser(tirer(), vrai)) return;
        }
        lignes.push(repli); bonnes.push(vrai ? 0 : 1);
    };

    // Trois lignes (règle prof 2026-09-23, seconde demande) : DEUX comparaisons
    // et UN encadrement. La vérité de chaque ligne est décidée d'avance, au
    // moins une vraie et une fausse par construction ; le symbole de la
    // comparaison et les bornes de l'encadrement en découlent.
    const verites = shuffle([true, false, randomChoice([true, false])]);
    // Au plus une comparaison en mode « frac » (fraction contre fraction, le
    // cas le plus coûteux : chercher un dénominateur commun) ; l'autre en
    // « dec » ou « ent », où une division ou la lecture de la partie entière
    // suffit. L'ordre est mélangé pour que la place ne dise rien du mode.
    const modes = shuffle([randomChoice(['dec', 'frac', 'ent']), randomChoice(['dec', 'ent'])]);
    // Chaque ligne est une formule KaTeX entière (\lt, \gt) ; le premier
    // nombre, souvent négatif, est groupé (cf. grp). Paires proches, même
    // contrainte que « completer » (revue 2026-09-22).
    const ligneComparaison = ({ A, B }, vrai) => {
        const inf = cmpRat(A.r, B.r) < 0;               // A < B ?
        return tex(`${grp(frag(A))} ${inf === vrai ? '\\lt' : '\\gt'} ${frag(B)}`);
    };
    modes.forEach((mode, i) => {
        ajouter(() => ligneComparaison(pairProche(mode), verites[i]),
            ligneComparaison(REPLI_PAIRE[mode], verites[i]), verites[i]);
    });
    // Un encadrement a < x < a + 1 : fraction ou décimal négatif (revue
    // 2026-09-22). x n'est jamais entier, donc les deux entiers qui l'encadrent
    // font une ligne vraie, et les mêmes décalés d'un cran (vers le bas ou vers
    // le haut) une ligne fausse.
    const ligneEncadrement = (x, vrai, sens) => {
        const a = floorDiv(x.r.p, x.r.q) + (vrai ? 0 : sens);
        return tex(`${grp(String(a))} \\lt ${frag(x)} \\lt ${a + 1}`);
    };
    const vraiEnc = verites[2];
    ajouter(() => {
        const x = randomChoice([true, false]) ? vFrac(fracAlea()) : vDec(decAlea(-1));
        return ligneEncadrement(x, vraiEnc, randomChoice([-1, 1]));
    }, ligneEncadrement(vFrac({ p: -7, q: 3 }), vraiEnc, -1), vraiEnc);
    const ordre = shuffle(lignes.map((_, i) => i));
    const L = ordre.map(i => lignes[i]);
    const B = ordre.map(i => bonnes[i]);
    const payload = makeVraiFaux(L, B,
        `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>`, opts(L_VF));
    payload._meta.params = { lignes: L, bonnes: B, modes };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    completer_qcm_unique: genCompleterQcm,
    ranger_ordonner: genRangerOrdonner,
    encadrer_texte_trou_saisie: genEncadrerTrous,
    vf_tableau_vf: genVfTableau,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', ordonner: 'Ranger', texte_trou_saisie: 'Saisie trous', tableau_vf: 'Vrai/Faux',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'completer', label: L_COMPLETER, formatsList: buildFormats('completer') },
    { id: 'ranger', label: L_RANGER, formatsList: buildFormats('ranger') },
    { id: 'encadrer', label: L_ENCADRER, formatsList: buildFormats('encadrer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
